import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";

import { firestore, storage } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  ref as storageRef,
} from "firebase/storage";

/* ---------- UI ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.1fr 0.9fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
`;

const Label = styled.div`
  font-weight: 950;
  margin: 10px 0 6px;
  color: #111827;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  background: #fff;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  border: 1px solid rgba(233, 78, 27, 0.28);
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const Preview = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  background: #000;
`;

const Img = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  display: block;
`;

const Pdf = styled.embed`
  width: 100%;
  height: 280px;
  display: block;
  background: #fff;
`;

/* ---------- Helpers ---------- */

function isPdfByUrl(url) {
  const s = (url || "").toLowerCase();
  return s.includes(".pdf") || s.includes("application/pdf");
}

function safeStorageName(name) {
  return (name || "intyg")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");
}

function makeStoragePath(file) {
  return `intyg/${Date.now()}-${safeStorageName(file?.name)}`;
}

/**
 * 🔥 Viktigt:
 * Om doc saknar storagePath försöker vi radera via URL:
 * - Firebase Storage kan radera via "refFromURL" (i SDK v9 heter det ref(storage, url) och funkar med gs:// eller https URL)
 * - Ibland kan det faila => då raderar vi bara Firestore-doc.
 */
async function tryDeleteStorageFile(storage, it) {
  // 1) Bästa: storagePath (framtida docs)
  if (it.storagePath) {
    await deleteObject(ref(storage, it.storagePath));
    return;
  }

  // 2) För gamla docs: försök via URL
  if (it.fileUrl) {
    // ref(storage, https-url) funkar i många fall på Firebase v9+.
    // Om det failar fångar vi det högre upp.
    await deleteObject(ref(storage, it.fileUrl));
  }
}

/* ---------- Component ---------- */

export default function AdminIntyg() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const [file, setFile] = useState(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  const previewIsPdf = useMemo(
    () => (file ? file.type === "application/pdf" || isPdfByUrl(file.name) : false),
    [file]
  );

  const load = async () => {
    // Din collection heter "intyg" (inte settings)
    const snap = await getDocs(collection(firestore, "intyg"));
    // Om du vill sortera: lägg createdAt framåt och sortera lokalt annars
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // lite snyggare: nyaste först om createdAt finns (annars behåller ordning)
    arr.sort((a, b) => {
      const ta = a.createdAt?.seconds || 0;
      const tb = b.createdAt?.seconds || 0;
      return tb - ta;
    });
    setItems(arr);
  };

  useEffect(() => {
    load();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => setFile(null);

  const upload = async () => {
    if (!file) return alert("Välj en fil (bild eller PDF).");

    setBusy(true);
    try {
      // ✅ spara i Storage -> intyg/
      const storagePath = makeStoragePath(file);
      const r = storageRef(storage, storagePath);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);

      // ✅ din befintliga DB har fileUrl – vi sparar det som tidigare
      // + vi lägger till storagePath framåt (så delete blir 100%)
      await addDoc(collection(firestore, "intyg"), {
        fileUrl: url,
        storagePath,
        fileName: file.name || null,
        contentType: file.type || null,
        createdAt: serverTimestamp(),
      });

      reset();
      await load();
      alert("Intyg uppladdat!");
    } catch (e) {
      alert(e?.message || "Kunde inte ladda upp intyg.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Är du säker på att du vill ta bort detta intyg?")) return;

    setBusy(true);
    try {
      // 1) ta bort Firestore-doc
      await deleteDoc(doc(firestore, "intyg", it.id));

      // 2) försök ta bort fil i Storage
      try {
        await tryDeleteStorageFile(storage, it);
      } catch {
        // Om gamla docs saknar storagePath och URL-delete failar:
        // vi låter Firestore vara rensat ändå (viktigast).
      }

      await load();
      alert("Intyg raderat!");
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort intyg.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Intyg (upload)">
      <Grid>
        <Card>
          <h3 style={{ margin: 0 }}>Ladda upp intyg</h3>

          <Label>Fil (bild eller PDF)</Label>
          <Input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />

          {previewUrl ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 950, marginBottom: 8 }}>Förhandsvisning</div>
              <Preview>
                {previewIsPdf ? (
                  <Pdf src={previewUrl} type="application/pdf" />
                ) : (
                  <Img src={previewUrl} alt="Förhandsvisning" />
                )}
              </Preview>
            </div>
          ) : (
            <Muted style={{ marginTop: 10 }}>Välj en fil för att se förhandsvisning.</Muted>
          )}

          <Row>
            <Btn type="button" onClick={upload} disabled={busy}>
              {busy ? "Laddar upp…" : "Ladda upp"}
            </Btn>
            <Ghost type="button" onClick={reset} disabled={busy}>
              Rensa
            </Ghost>
          </Row>

          <Muted style={{ marginTop: 10 }}>
            Firestore: <b>intyg</b> ({`fileUrl`}). Storage: <b>intyg/</b>.
          </Muted>
        </Card>

        <Card>
          <h3 style={{ margin: 0 }}>Uppladdade intyg</h3>
          <div style={{ height: 10 }} />

          {items.length === 0 ? (
            <Muted>Inga intyg ännu.</Muted>
          ) : (
            items.map((it) => {
              const pdf = isPdfByUrl(it.fileUrl) || it.contentType === "application/pdf";
              return (
                <Item key={it.id}>
                  <Preview>
                    {pdf ? (
                      <Pdf src={it.fileUrl} type="application/pdf" />
                    ) : (
                      <Img src={it.fileUrl} alt="Intyg" />
                    )}
                  </Preview>

                  <Muted>
                    {it.fileName ? (
                      <>
                        Fil: <b>{it.fileName}</b>
                      </>
                    ) : (
                      <>Fil</>
                    )}
                  </Muted>

                  <Row>
                    <Ghost as="a" href={it.fileUrl} target="_blank" rel="noreferrer">
                      Öppna
                    </Ghost>
                    <Danger type="button" onClick={() => remove(it)} disabled={busy}>
                      Ta bort
                    </Danger>
                  </Row>
                </Item>
              );
            })
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}

